import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../../lib/api/client'
import type { ChangeSetResponse, Project } from '../../../lib/api/types'
import { useEditorStore } from '../../../stores/editorStore'
import { ChangeReview } from '../ChangeReview'

type Tool = 'auto' | 'silence' | 'fillers' | 'captions' | 'zoom' | 'reframe' | 'voice' | 'prompt'

export function AiPanel({ project }: { project: Project }) {
  const queryClient = useQueryClient()
  const sequence = useEditorStore((s) => s.sequence)
  const [tool, setTool] = useState<Tool>('auto')
  const [changeSet, setChangeSet] = useState<ChangeSetResponse | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const sequenceId = sequence?.id

  const run = useMutation({
    mutationFn: async ({ path, body }: { path: string; body: unknown }) =>
      api.post<ChangeSetResponse & Record<string, unknown>>(`/sequences/${sequenceId}${path}`, body),
    onSuccess: (result) => {
      setMessage(null)
      if (result.changeSetId) {
        setChangeSet(result)
      } else {
        // Ferramentas que aplicam direto (legendas, zoom) não geram ChangeSet.
        setMessage(result.summary ?? 'Concluído.')
        void reload()
      }
    },
    onError: (error) => {
      setMessage(null)
      useEditorStore.getState().setError(
        error instanceof ApiError ? error.message : 'A operação de IA falhou',
      )
    },
  })

  async function reload() {
    if (!sequenceId) return
    const fresh = await api.get<{ sequence: NonNullable<typeof sequence> }>(`/sequences/${sequenceId}`)
    useEditorStore.getState().load(fresh.sequence, project.id)
    void queryClient.invalidateQueries({ queryKey: ['sequence', sequenceId] })
  }

  if (!sequenceId) return null

  return (
    <div className="p-3 flex flex-col gap-3">
      <div>
        <h2 className="text-xs font-semibold text-ink-100">Ferramentas de IA</h2>
        <p className="mt-1 text-2xs text-ink-400 leading-relaxed">
          Tudo que a IA propõe aparece na timeline e continua editável. Nada é aplicado sem
          você ver.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-1">
        {TOOLS.map((entry) => (
          <button
            key={entry.id}
            onClick={() => { setTool(entry.id); setChangeSet(null); setMessage(null) }}
            className={`rounded px-2 py-1.5 text-2xs text-left transition-colors
              ${tool === entry.id ? 'bg-ink-750 text-accent' : 'text-ink-300 hover:bg-ink-800'}`}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <div className="border-t border-ink-750 pt-3">
        {tool === 'auto' && <AutoEditForm onRun={(body) => run.mutate({ path: '/ai/auto-edit', body })} busy={run.isPending} />}
        {tool === 'silence' && <SilenceForm onRun={(body) => run.mutate({ path: '/ai/silence-removal', body })} busy={run.isPending} />}
        {tool === 'fillers' && <FillerForm onRun={(body) => run.mutate({ path: '/ai/filler-removal', body })} busy={run.isPending} />}
        {tool === 'captions' && <CaptionForm onRun={(body) => run.mutate({ path: '/ai/captions', body })} busy={run.isPending} />}
        {tool === 'zoom' && <ZoomForm onRun={(body) => run.mutate({ path: '/ai/smart-zoom', body })} busy={run.isPending} />}
        {tool === 'reframe' && <ReframeForm onRun={(body) => run.mutate({ path: '/ai/auto-reframe', body })} busy={run.isPending} />}
        {tool === 'voice' && <VoiceForm onRun={(body) => run.mutate({ path: '/ai/enhance-voice', body })} busy={run.isPending} />}
        {tool === 'prompt' && <PromptForm onRun={(body) => run.mutate({ path: '/ai/prompt', body })} busy={run.isPending} />}
      </div>

      {message && (
        <p className="text-2xs text-teal bg-teal-soft rounded px-2.5 py-2 whitespace-pre-line">{message}</p>
      )}

      {changeSet?.changeSetId && (
        <ChangeReview
          changeSet={changeSet}
          onResolved={() => { setChangeSet(null); void reload() }}
        />
      )}
    </div>
  )
}

const TOOLS: Array<{ id: Tool; label: string }> = [
  { id: 'auto', label: '✦ Auto Edit' },
  { id: 'prompt', label: '✦ Editar por prompt' },
  { id: 'silence', label: 'Remover silêncios' },
  { id: 'fillers', label: 'Remover vícios' },
  { id: 'captions', label: 'Gerar legendas' },
  { id: 'zoom', label: 'Smart Zoom' },
  { id: 'reframe', label: 'Auto Reframe' },
  { id: 'voice', label: 'Enhance Voice' },
]

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="label">{label}</span>
      {children}
    </label>
  )
}

function RunButton({ busy, children }: { busy: boolean; children: React.ReactNode }) {
  return (
    <button type="submit" className="btn-primary justify-center py-2 mt-1" disabled={busy}>
      {busy ? 'Analisando…' : children}
    </button>
  )
}

function AutoEditForm({ onRun, busy }: { onRun: (body: unknown) => void; busy: boolean }) {
  const [goal, setGoal] = useState('REELS')
  const [pace, setPace] = useState('DINAMICO')
  const [aspect, setAspect] = useState('9:16')
  const [target, setTarget] = useState('')

  return (
    <form
      className="flex flex-col gap-2.5"
      onSubmit={(e) => {
        e.preventDefault()
        onRun({
          goal, pace, aspectRatio: aspect,
          targetDuration: target ? Number(target) : null,
        })
      }}
    >
      <Field label="Objetivo">
        <select id="ae-goal" className="field" value={goal} onChange={(e) => setGoal(e.target.value)}>
          {['REELS', 'TIKTOK', 'SHORTS', 'YOUTUBE', 'PODCAST', 'ANUNCIO', 'INSTITUCIONAL', 'UGC'].map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      </Field>
      <Field label="Duração alvo (s) — vazio = automática">
        <input id="ae-duration" className="field" type="number" min={5} value={target}
               onChange={(e) => setTarget(e.target.value)} placeholder="automática" />
      </Field>
      <Field label="Formato">
        <select id="ae-aspect" className="field" value={aspect} onChange={(e) => setAspect(e.target.value)}>
          {['9:16', '16:9', '4:5', '1:1'].map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      </Field>
      <Field label="Ritmo">
        <select id="ae-pace" className="field" value={pace} onChange={(e) => setPace(e.target.value)}>
          <option value="NATURAL">Natural</option>
          <option value="DINAMICO">Dinâmico</option>
          <option value="MUITO_DINAMICO">Muito dinâmico</option>
        </select>
      </Field>
      <RunButton busy={busy}>Analisar e propor edição</RunButton>
    </form>
  )
}

function SilenceForm({ onRun, busy }: { onRun: (body: unknown) => void; busy: boolean }) {
  const [minSilence, setMinSilence] = useState(0.5)
  const [padding, setPadding] = useState(0.25)

  return (
    <form className="flex flex-col gap-2.5" onSubmit={(e) => {
      e.preventDefault()
      onRun({ minSilence, padding, preview: true })
    }}>
      <Field label="Silêncio mínimo">
        <select id="sil-min" className="field" value={minSilence}
                onChange={(e) => setMinSilence(Number(e.target.value))}>
          {[0.2, 0.3, 0.5, 0.75, 1].map((v) => (
            <option key={v} value={v}>{v < 1 ? `${v * 1000}ms` : `${v}s`}</option>
          ))}
        </select>
      </Field>
      <Field label={`Silêncio preservado: ${(padding * 1000).toFixed(0)}ms`}>
        <input id="sil-pad" type="range" min={0} max={0.6} step={0.05} value={padding}
               onChange={(e) => setPadding(Number(e.target.value))} className="accent-accent" />
      </Field>
      <p className="text-2xs text-ink-500 leading-relaxed">
        O corte acontece entre palavras da transcrição — nunca no meio de uma.
      </p>
      <RunButton busy={busy}>Detectar silêncios</RunButton>
    </form>
  )
}

function FillerForm({ onRun, busy }: { onRun: (body: unknown) => void; busy: boolean }) {
  const [level, setLevel] = useState('NORMAL')
  const [removeRepetitions, setRepetitions] = useState(true)
  const [removeFalseStarts, setFalseStarts] = useState(false)

  return (
    <form className="flex flex-col gap-2.5" onSubmit={(e) => {
      e.preventDefault()
      onRun({ level, removeFillers: true, removeRepetitions, removeFalseStarts, preview: true })
    }}>
      <Field label="Nível">
        <select id="fil-level" className="field" value={level} onChange={(e) => setLevel(e.target.value)}>
          <option value="CONSERVADOR">Conservador — só hesitações</option>
          <option value="NORMAL">Normal — inclui muletas</option>
          <option value="AGRESSIVO">Agressivo — inclui conectivos</option>
        </select>
      </Field>
      <Checkbox id="fil-rep" checked={removeRepetitions} onChange={setRepetitions}>
        Remover repetições (gagueira)
      </Checkbox>
      <Checkbox id="fil-fs" checked={removeFalseStarts} onChange={setFalseStarts}>
        Remover tentativas refeitas
      </Checkbox>
      <RunButton busy={busy}>Detectar vícios</RunButton>
    </form>
  )
}

function CaptionForm({ onRun, busy }: { onRun: (body: unknown) => void; busy: boolean }) {
  const [style, setStyle] = useState('karaoke')
  const [maxWords, setMaxWords] = useState(5)
  const [highlight, setHighlight] = useState(true)

  return (
    <form className="flex flex-col gap-2.5" onSubmit={(e) => {
      e.preventDefault()
      onRun({ style, maxWordsPerLine: maxWords, highlightKeywords: highlight })
    }}>
      <Field label="Estilo">
        <select id="cap-style" className="field" value={style} onChange={(e) => setStyle(e.target.value)}>
          <option value="sentence">Sentença</option>
          <option value="word">Palavra por palavra</option>
          <option value="karaoke">Karaokê</option>
          <option value="tiktok">TikTok / Reels</option>
        </select>
      </Field>
      <Field label={`Palavras por linha: ${maxWords}`}>
        <input id="cap-words" type="range" min={1} max={10} value={maxWords}
               onChange={(e) => setMaxWords(Number(e.target.value))} className="accent-accent" />
      </Field>
      <Checkbox id="cap-hl" checked={highlight} onChange={setHighlight}>
        Destacar palavras-chave
      </Checkbox>
      <RunButton busy={busy}>Gerar legendas</RunButton>
    </form>
  )
}

function ZoomForm({ onRun, busy }: { onRun: (body: unknown) => void; busy: boolean }) {
  const [intensity, setIntensity] = useState('MEDIA')
  const [frequency, setFrequency] = useState('MEDIA')

  return (
    <form className="flex flex-col gap-2.5" onSubmit={(e) => {
      e.preventDefault()
      onRun({ intensity, frequency })
    }}>
      <Field label="Intensidade">
        <select id="zoom-int" className="field" value={intensity} onChange={(e) => setIntensity(e.target.value)}>
          <option value="BAIXA">Baixa (105%)</option>
          <option value="MEDIA">Média (109%)</option>
          <option value="ALTA">Alta (115%)</option>
        </select>
      </Field>
      <Field label="Frequência">
        <select id="zoom-freq" className="field" value={frequency} onChange={(e) => setFrequency(e.target.value)}>
          <option value="BAIXA">Baixa</option>
          <option value="MEDIA">Média</option>
          <option value="ALTA">Alta</option>
        </select>
      </Field>
      <p className="text-2xs text-ink-500 leading-relaxed">
        Cada zoom vira keyframes editáveis — você pode ajustar ou apagar qualquer um.
      </p>
      <RunButton busy={busy}>Aplicar Smart Zoom</RunButton>
    </form>
  )
}

function ReframeForm({ onRun, busy }: { onRun: (body: unknown) => void; busy: boolean }) {
  const [aspect, setAspect] = useState('9:16')
  return (
    <form className="flex flex-col gap-2.5" onSubmit={(e) => { e.preventDefault(); onRun({ aspectRatio: aspect }) }}>
      <Field label="Converter para">
        <select id="rf-aspect" className="field" value={aspect} onChange={(e) => setAspect(e.target.value)}>
          {['9:16', '4:5', '1:1', '16:9'].map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      </Field>
      <RunButton busy={busy}>Reenquadrar</RunButton>
    </form>
  )
}

function VoiceForm({ onRun, busy }: { onRun: (body: unknown) => void; busy: boolean }) {
  const [denoise, setDenoise] = useState(true)
  const [dereverb, setDereverb] = useState(true)
  const [normalize, setNormalize] = useState(true)

  return (
    <form className="flex flex-col gap-2.5" onSubmit={(e) => {
      e.preventDefault()
      onRun({ denoise, dereverb, deesser: true, normalize, targetLufs: -14 })
    }}>
      <Checkbox id="v-dn" checked={denoise} onChange={setDenoise}>Reduzir ruído</Checkbox>
      <Checkbox id="v-dr" checked={dereverb} onChange={setDereverb}>Reduzir eco</Checkbox>
      <Checkbox id="v-nm" checked={normalize} onChange={setNormalize}>Nivelar volume (−14 LUFS)</Checkbox>
      <p className="text-2xs text-ink-500 leading-relaxed">
        O processamento acontece no render. A intenção fica registrada na timeline e é
        reversível.
      </p>
      <RunButton busy={busy}>Aprimorar voz</RunButton>
    </form>
  )
}

function PromptForm({ onRun, busy }: { onRun: (body: unknown) => void; busy: boolean }) {
  const [prompt, setPrompt] = useState('')
  const examples = [
    'Remova todos os silêncios maiores que 500ms',
    'Remova as partes repetidas',
    'Deixe esse vídeo 15% mais rápido',
    'Coloque zoom nos momentos mais importantes',
  ]

  return (
    <form className="flex flex-col gap-2.5" onSubmit={(e) => {
      e.preventDefault()
      if (prompt.trim()) onRun({ prompt: prompt.trim() })
    }}>
      <Field label="O que você quer fazer?">
        <textarea
          id="ai-prompt"
          className="field min-h-[70px] resize-y"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Remova todos os silêncios maiores que 500ms"
        />
      </Field>
      <div className="flex flex-wrap gap-1">
        {examples.map((example) => (
          <button
            key={example}
            type="button"
            className="text-[9px] rounded bg-ink-800 hover:bg-ink-750 text-ink-400 px-1.5 py-1"
            onClick={() => setPrompt(example)}
          >
            {example}
          </button>
        ))}
      </div>
      <RunButton busy={busy}>Interpretar</RunButton>
    </form>
  )
}

function Checkbox({
  id, checked, onChange, children,
}: {
  id: string
  checked: boolean
  onChange: (value: boolean) => void
  children: React.ReactNode
}) {
  return (
    <label className="flex items-center gap-2 text-2xs text-ink-300 cursor-pointer">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-accent"
      />
      {children}
    </label>
  )
}
